export const NOTIFICATION_WINDOW_HEIGHT = 160;
export const NOTIFICATION_WINDOW_WIDTH = 365;

export interface AppUrlType {
  app: {
    url: string;
  };
}

export interface RegistrySettingItem {
  key: string;
  value: string | undefined;
}

// SECURITY (VULN-2157): only exposed by desktop builds older than #4682, whose registry
// bridge is vulnerable to command injection. Temporary: delete once the patched desktop app
// is deployed everywhere.
export interface LegacyRegistryAccessorItem {
  key: string;
  get: (name: string) => Promise<{ value?: string | null }>;
}

export type RegistryPair = [keyof DesktopSettings, number | boolean | string];

export type RegistryPairAlike = [string, number | boolean | string];

export interface OpenDialogSyncOptions {
  title?: string;
  defaultPath?: string;
  buttonLabel?: string;
  filters?: Array<{ name: string; extensions: string[] }>;
  properties?: Array<
    | 'openFile'
    | 'openDirectory'
    | 'multiSelections'
    | 'showHiddenFiles'
    | 'createDirectory'
    | 'promptToCreate'
    | 'noResolveAliases'
    | 'treatPackageAsDirectory'
    | 'dontAddToRecent'
  >;
  message?: string;
  securityScopedBookmarks?: boolean;
}

export type UserDataFileName = 'v4-settings.json' | 'system.json';

// Base types
export type PathName = 'home' | 'appData' | 'userData';

// Window related types
export interface BrowserWindowOptions {
  alwaysOnTop?: boolean;
  autoHideMenuBar?: boolean;
  closable?: boolean;
  frame?: boolean;
  fullscreenable?: boolean;
  height?: number;
  maximizable?: boolean;
  resizable?: boolean;
  show?: boolean;
  width?: number;
  onDidFinishLoad?: () => void;
}

// Settings types
export enum ProxyMode {
  NoProxy = 'no-proxy',
  System = 'system',
  Manual = 'manual',
}

export interface DesktopConnectionSettings {
  pacUrl: string;
  proxyMode: ProxyMode;
  proxyUrl: string;
  ssoConnection: string;
  username: string;
}

export interface ImportWatcherLocalSettings {
  isImportWatcherActivated: boolean;
  printerFolderName?: string;
}

export interface OtherDesktopSettings {
  autoUpdateEnabled: boolean;
  hideSupport: boolean;
  myRequestsOnly: boolean;
}

export interface QADesktopSettings {
  releaseServerURL?: string;
}

export interface WatcherLocalSettings {
  keepFileDuringXDays: number;
  stabilityThreshold: number;
  usePollingInterval: number | false;
  watchPath: string;
  usePolling: boolean;
}

export type DesktopSettings = DesktopConnectionSettings &
  ImportWatcherLocalSettings &
  OtherDesktopSettings &
  QADesktopSettings &
  WatcherLocalSettings;

// Storage related types
export interface ClearSessionStorageDataOptions {
  origin?: string;
  storages?: Array<
    | 'cookies'
    | 'filesystem'
    | 'indexdb'
    | 'localstorage'
    | 'shadercache'
    | 'websql'
    | 'serviceworkers'
    | 'cachestorage'
  >;
}
export interface FileWatcher {
  on: (event: string, listener: (...args: any[]) => void) => FileWatcher;
  off: (event: string, listener: (...args: any[]) => void) => FileWatcher;
  close: () => void;
  closed: () => boolean;
  getWatched: () => Record<string, unknown>;
  add: (path: string) => FileWatcher;
  unwatch: (path: string) => FileWatcher;
}

export interface FileWatcher {
  on: (event: string, listener: (...args: any[]) => void) => FileWatcher;
  off: (event: string, listener: (...args: any[]) => void) => FileWatcher;
  close: () => void;
  closed: () => boolean;
  getWatched: () => Record<string, unknown>;
  add: (path: string) => FileWatcher;
  unwatch: (path: string) => FileWatcher;
}

// File system related types
export interface EnsureDirCallbacks {
  onFailedToCreateDir?: (path: string, err: Error) => void;
  onDirAlreadyExists?: (path: string) => void;
  onStartCreatingDir?: (path: string) => void;
  onPathIsNotDir?: (path: string, result: any) => void;
}

// Native Image type (simplified)
export interface NativeImage {
  isEmpty(): boolean;
  getSize(): { width: number; height: number };
  toDataURL(options?: { scaleFactor?: number }): string;
  toJSON(): string;
}

// Bridge API type
export interface BridgeApi {
  clearCache: (sessionPartition?: string) => Promise<void>;
  clearSessionStorageData: (params: {
    partition?: string;
    options: ClearSessionStorageDataOptions;
  }) => Promise<void>;
  createNotificationWindow?: (url: string, options?: BrowserWindowOptions) => any;
  createNotificationWindowAsync?: (url: string, options?: BrowserWindowOptions) => Promise<any>;
  createSettingsWindow?: (url: string, options?: BrowserWindowOptions) => any;
  createSettingsWindowAsync?: (url: string, options?: BrowserWindowOptions) => Promise<any>;
  ensureDir: (dirPath: string, callbacks?: EnsureDirCallbacks) => Promise<void>;
  ensureVirtualPrinterLink?: (folderName: string) => Promise<boolean>;
  exitApp: () => void;
  focusCurrentWindow: () => void;
  getAllFilePathsFromDir?: (dirPath: string) => string[]; // Deprecated, will be removed in future versions
  getAllFilePathsFromDirAsync?: (dirPath: string) => Promise<string[]>;
  getArch?: () => string; // Deprecated, will be removed in future versions
  getArchAsync?: () => Promise<string>;
  getAutoUpdater: () => any;
  getBaseName?: (filePath: string) => string; // Deprecated, will be removed in future versions
  getBaseNameAsync?: (filePath: string) => Promise<string>;
  getElectronVersion?: () => string; // Deprecated, will be removed in future versions
  getElectronVersionAsync?: () => Promise<string>;
  getIpcRenderer: () => any;
  // TODO(VULN-2157): getLifenSettingsFromRegistry and configurePdfCreatorPrinter are optional only
  // for desktop builds older than #4682; make them required again once those are gone
  getLifenSettingsFromRegistry?: () => Promise<RegistrySettingItem[]>;
  configurePdfCreatorPrinter?: () => Promise<void>;
  // TODO(VULN-2157): remove with LegacyRegistryAccessorItem
  getLifenSettingsRegistryAccessor?: () => Promise<LegacyRegistryAccessorItem[]>;
  getHomedir?: () => string; // Deprecated, will be removed in future versions
  getHomedirAsync?: () => Promise<string>;
  getLogger: () => any;
  getNativeImageModule?: () => any; // Deprecated, will be removed in future versions
  getOsRelease?: () => string; // Deprecated, will be removed in future versions
  getOsReleaseAsync?: () => Promise<string>;
  getOsType?: () => string; // Deprecated, will be removed in future versions
  getOsTypeAsync?: () => Promise<string>;
  getPath?: (name: PathName) => string; // Deprecated, will be removed in future versions
  getPathAsync?: (name: PathName) => Promise<string>;
  getPlatform?: () => string; // Deprecated, will be removed in future versions
  getPlatformAsync?: () => Promise<string>;
  hideCurrentWindow: () => void;
  initFileWatcher?: (options?: any) => Promise<FileWatcher>;
  initFileWatcherAsync?: (options?: any) => Promise<FileWatcher>;
  isExecPathDirectory?: (dirPath: string) => boolean; // Deprecated, will be removed in future versions
  isExecPathDirectoryAsync?: (dirPath: string) => Promise<boolean>;
  isUpdateAvailable: (updateFileName?: string) => Promise<boolean> | boolean;
  joinPaths?: (...paths: string[]) => string; // Deprecated, will be removed in future versions
  joinPathsAsync?: (...paths: string[]) => Promise<string>;
  logout: () => void;
  makeTrayMenu: (data: { icon: string | NativeImage; menuItems: Record<string, any>[] }) => void;
  moveFile: (
    location: string,
    destination: string,
    options: { overwrite?: boolean },
  ) => Promise<void>;
  openLink: (url: string) => void;
  quitApp: () => void;
  readFileInWatchPath?: (fileName: string) => Buffer; // Deprecated, will be removed in future versions
  readFileInWatchPathAsync?: (fileName: string) => Promise<Buffer>;
  readLocalSettings: () => Promise<DesktopSettings> | DesktopSettings;
  redirectTo: (url: string) => void;
  removeFile: (filePath: string) => Promise<void>;
  resizeCurrentWindow: (width: number, height: number, animate?: boolean) => void;
  restoreCurrentWindow: () => void;
  selectDirectory?: (options: OpenDialogSyncOptions) => string[] | undefined; // Deprecated, will be removed in future versions
  selectDirectoryAsync?: (options: OpenDialogSyncOptions) => Promise<string[] | undefined>;
  showCurrentWindow: () => void;
  showItemInFolder: (dirPath: string) => void;
  statFile?: (filePath: string) => any; // Deprecated, will be removed in future versions
  statFileAsync?: (filePath: string) => Promise<any>;
  writeLocalSettings: (settingsData: DesktopSettings) => void;
  writeLocalUrl: (urlData: AppUrlType) => void;
  saveUserEmail: (email: string) => Promise<void>;
  switchWorkspace: (workspaceId: string) => Promise<void>;
}
